import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { getTitleForReader, getChapterBody, getChapterBodyAuthed, saveReadingProgress, getReadingProgress } from "@/lib/reader.functions";
import { CRYSTALLIZED_PREVIEW_BODIES } from "@/lib/crystallized-preview";
import { supabase } from "@/integrations/supabase/client";
import { track } from "@/lib/analytics";
import { SITE_URL } from "@/lib/site";
import { ArrowLeft, ArrowRight, BookOpen, ChevronDown, Lock, Type, Minus, Plus, Sun, Moon } from "lucide-react";

export const Route = createFileRoute("/library/read/$slug")({
  head: ({ params }) => ({
    meta: [
      { title: "Reader — KAfrequency Library" },
      { name: "description", content: "Immersive reading room for KAfrequency titles." },
      { name: "robots", content: "noindex" },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/library/read/${params.slug}` }],
  }),
  component: Reader,
});

type ChapterMeta = { id: string; ord: number; slug: string; name: string; word_count: number; is_preview: boolean };

function Reader() {
  const { slug } = Route.useParams();
  const nav = useNavigate();
  const search = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const chapterSlugFromUrl = search.get("c");

  const loadTitle = useServerFn(getTitleForReader);
  const loadChapter = useServerFn(getChapterBody);
  const loadChapterAuthed = useServerFn(getChapterBodyAuthed);
  const saveProgress = useServerFn(saveReadingProgress);
  const loadProgress = useServerFn(getReadingProgress);

  const [state, setState] = useState<{
    title: any;
    chapters: ChapterMeta[];
    activeChapter: ChapterMeta | null;
    body: string;
    locked: boolean;
    loading: boolean;
  }>({ title: null, chapters: [], activeChapter: null, body: "", locked: false, loading: true });

  const [fontScale, setFontScale] = useState(1);
  const [theme, setTheme] = useState<"dark" | "sepia">("dark");
  const [tocOpen, setTocOpen] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);
  const startedAt = useRef<number>(Date.now());
  const [authed, setAuthed] = useState<boolean>(false);

  // Persist reader settings
  useEffect(() => {
    try {
      const s = JSON.parse(localStorage.getItem("kaf_reader_settings") || "{}");
      if (s.fontScale) setFontScale(s.fontScale);
      if (s.theme) setTheme(s.theme);
    } catch {}
    supabase.auth.getUser().then(({ data }) => setAuthed(!!data.user));
  }, []);
  useEffect(() => {
    localStorage.setItem("kaf_reader_settings", JSON.stringify({ fontScale, theme }));
  }, [fontScale, theme]);

  // Load title + chapters
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await loadTitle({ data: { slug } });
      if (cancelled || !res) return setState((s) => ({ ...s, loading: false }));
      const chapters = res.chapters as ChapterMeta[];
      let target = chapterSlugFromUrl
        ? chapters.find((c) => c.slug === chapterSlugFromUrl)
        : null;
      if (!target && authed) {
        try {
          const prog = await loadProgress({ data: { titleId: res.title.id } });
          if (prog?.last_chapter_id) target = chapters.find((c) => c.id === prog.last_chapter_id) ?? null;
        } catch {}
      }
      if (!target) target = chapters[0] ?? null;
      setState((s) => ({ ...s, title: res.title, chapters, activeChapter: target, loading: false }));
      if (target) loadChapterBody(target, res.title);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, authed]);

  async function loadChapterBody(chapter: ChapterMeta, title: any) {
    startedAt.current = Date.now();
    track("chapter_open", { title: slug, chapter: chapter.slug });
    const row = authed
      ? await loadChapterAuthed({ data: { titleSlug: slug, chapterSlug: chapter.slug } })
      : await loadChapter({ data: { titleSlug: slug, chapterSlug: chapter.slug } });
    let body = row?.body_md ?? "";
    let locked = false;
    if (!body) {
      // Fall back to preview data module (only for The Crystallized Mind ch 1-2)
      if (slug === "the-crystallized-mind" && CRYSTALLIZED_PREVIEW_BODIES[chapter.ord]) {
        body = CRYSTALLIZED_PREVIEW_BODIES[chapter.ord];
      } else if (!chapter.is_preview) {
        locked = true;
      }
    }
    setState((s) => ({ ...s, activeChapter: chapter, body, locked }));
    window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
  }

  function goToChapter(c: ChapterMeta) {
    if (!state.title) return;
    setTocOpen(false);
    nav({ to: "/library/read/$slug", params: { slug }, search: { c: c.slug } as any });
    loadChapterBody(c, state.title);
  }

  const currentIdx = useMemo(
    () => state.chapters.findIndex((c) => c.id === state.activeChapter?.id),
    [state.chapters, state.activeChapter],
  );
  const prev = currentIdx > 0 ? state.chapters[currentIdx - 1] : null;
  const next = currentIdx >= 0 && currentIdx < state.chapters.length - 1 ? state.chapters[currentIdx + 1] : null;

  // Auto-save progress
  useEffect(() => {
    if (!authed || !state.title || !state.activeChapter) return;
    const timer = setInterval(() => {
      const el = bodyRef.current;
      if (!el) return;
      const scrolled = window.scrollY + window.innerHeight;
      const total = el.offsetTop + el.scrollHeight;
      if (!state.activeChapter) return;
      const chapterProgress = Math.min(1, Math.max(0, scrolled / total));
      const overall = ((currentIdx + chapterProgress) / Math.max(1, state.chapters.length)) * 100;
      const seconds = Math.round((Date.now() - startedAt.current) / 1000);
      saveProgress({
        data: {
          titleId: state.title.id,
          chapterId: state.activeChapter.id,
          offset: Math.round(chapterProgress * 1000),
          percent: overall,
          secondsRead: seconds,
        },
      }).catch(() => {});
    }, 10000);
    return () => clearInterval(timer);
  }, [authed, state.title, state.activeChapter, currentIdx, state.chapters.length, saveProgress]);

  // Keyboard nav
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target && (e.target as HTMLElement).closest("input, textarea, [contenteditable]")) return;
      if (e.key === "ArrowLeft" && prev) goToChapter(prev);
      if (e.key === "ArrowRight" && next) goToChapter(next);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prev, next]);

  if (state.loading) {
    return <div className="mx-auto max-w-3xl px-6 py-24 text-center text-muted-foreground">Opening the chamber…</div>;
  }
  if (!state.title) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-24 text-center">
        <p className="text-muted-foreground">Title not found.</p>
        <Link to="/library" className="mt-4 inline-block text-gold-luminous">← Return to Library</Link>
      </div>
    );
  }

  const readingTimeMin = Math.max(1, Math.round((state.activeChapter?.word_count ?? 0) / 220));
  const overallPercent = state.chapters.length ? Math.round(((currentIdx + 1) / state.chapters.length) * 100) : 0;

  const themeBg = theme === "sepia" ? "bg-[#F5EFE1] text-[#2a1f10]" : "bg-obsidian text-ivory";
  const themeMuted = theme === "sepia" ? "text-[#5b4a2d]" : "text-muted-foreground";

  return (
    <div className={`min-h-[100dvh] ${themeBg} transition-colors`}>
      {/* Reader chrome */}
      <header className="sticky top-0 z-30 border-b border-gold/20 bg-obsidian/85 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center gap-3 px-4 py-3">
          <Link to="/library" className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.2em] text-gold hover:text-gold-luminous">
            <ArrowLeft size={14} /> Library
          </Link>
          <div className="mx-2 h-4 w-px bg-gold/25" />
          <button
            onClick={() => setTocOpen((o) => !o)}
            className="flex items-center gap-2 text-sm text-ivory hover:text-gold"
            aria-expanded={tocOpen}
          >
            <BookOpen size={14} className="text-gold" />
            <span className="font-serif">{state.title.title}</span>
            <ChevronDown size={14} className={`transition-transform ${tocOpen ? "rotate-180" : ""}`} />
          </button>
          <div className="ml-auto flex items-center gap-2 text-xs text-muted-foreground">
            <span className="hidden sm:inline">{overallPercent}%</span>
            <span className="hidden sm:inline">·</span>
            <span>~{readingTimeMin} min</span>
            <div className="ml-2 flex items-center gap-1 rounded-full border border-gold/30 px-1">
              <button aria-label="Decrease font" onClick={() => setFontScale((f) => Math.max(0.85, f - 0.05))} className="p-1 text-gold hover:text-gold-luminous"><Minus size={12} /></button>
              <Type size={12} className="text-gold" />
              <button aria-label="Increase font" onClick={() => setFontScale((f) => Math.min(1.4, f + 0.05))} className="p-1 text-gold hover:text-gold-luminous"><Plus size={12} /></button>
            </div>
            <button
              aria-label="Toggle theme"
              onClick={() => setTheme((t) => (t === "dark" ? "sepia" : "dark"))}
              className="rounded-full border border-gold/30 p-1.5 text-gold hover:text-gold-luminous"
            >
              {theme === "dark" ? <Sun size={12} /> : <Moon size={12} />}
            </button>
          </div>
        </div>
        {/* Progress bar */}
        <div className="h-0.5 w-full bg-gold/10">
          <div className="h-full bg-gold-gradient transition-all" style={{ width: `${overallPercent}%` }} />
        </div>
      </header>

      {/* TOC drawer */}
      {tocOpen && (
        <div className="fixed inset-0 z-40 flex" onClick={() => setTocOpen(false)}>
          <div className="w-full max-w-sm border-r border-gold/20 bg-obsidian p-6 shadow-2xl overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <p className="text-xs uppercase tracking-[0.3em] text-gold">Table of Contents</p>
            <h2 className="mt-2 font-serif text-2xl text-ivory">{state.title.title}</h2>
            <ol className="mt-6 space-y-1">
              {state.chapters.map((c) => (
                <li key={c.id}>
                  <button
                    onClick={() => goToChapter(c)}
                    className={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm ${
                      c.id === state.activeChapter?.id ? "bg-gold/10 text-gold-luminous" : "text-ivory/80 hover:bg-gold/5 hover:text-gold"
                    }`}
                  >
                    <span className="w-8 text-xs text-muted-foreground">{String(c.ord).padStart(2, "0")}</span>
                    <span className="flex-1">{c.name}</span>
                    {!c.is_preview && !authed && <Lock size={10} className="text-muted-foreground" />}
                  </button>
                </li>
              ))}
            </ol>
          </div>
          <div className="flex-1 bg-obsidian/70 backdrop-blur-sm" />
        </div>
      )}

      {/* Body */}
      <div className="mx-auto max-w-2xl px-5 py-12 sm:py-16">
        {state.activeChapter && (
          <>
            <p className={`text-xs uppercase tracking-[0.3em] ${themeMuted}`}>
              Chapter {state.activeChapter.ord} · ~{readingTimeMin} min
            </p>
            <h1 className="mt-2 font-serif text-3xl sm:text-4xl">{state.activeChapter.name}</h1>
            <div className="mt-3 h-px w-16 bg-gold/60" />
          </>
        )}

        {state.locked ? (
          <div className="mt-12 rounded-lg border border-gold/30 bg-obsidian p-8 text-center">
            <Lock className="mx-auto text-gold" size={28} />
            <p className="mt-4 font-serif text-lg text-ivory">This chapter is part of the full transmission.</p>
            <p className="mt-2 text-sm text-muted-foreground">
              Unlock <em>{state.title.title}</em> to receive all {state.chapters.length} chapters in your permanent library.
            </p>
            <Link
              to="/books/$slug"
              params={{ slug }}
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-gold-gradient px-6 py-3 text-sm font-semibold text-obsidian shadow-gold hover:shadow-halo"
            >
              Open product page
            </Link>
          </div>
        ) : (
          <article
            ref={bodyRef}
            className="prose prose-invert mt-8 max-w-none whitespace-pre-wrap font-serif leading-relaxed"
            style={{ fontSize: `${fontScale * 1.075}rem`, lineHeight: 1.85 }}
          >
            {state.body || (
              <p className={themeMuted}>
                This chapter body is being prepared. In the meantime, continue with the two preview chapters or purchase the full title from the product page.
              </p>
            )}
          </article>
        )}

        {/* Prev / Next */}
        <nav aria-label="Reading pagination" className="mt-16 flex items-center justify-between border-t border-gold/20 pt-8">
          {prev ? (
            <button onClick={() => goToChapter(prev)} className="group inline-flex items-center gap-2 text-sm text-gold hover:text-gold-luminous">
              <ArrowLeft size={14} className="transition-transform group-hover:-translate-x-0.5" />
              <span className="text-left">
                <span className={`block text-[10px] uppercase tracking-[0.25em] ${themeMuted}`}>Previous</span>
                <span className="font-serif">{prev.name}</span>
              </span>
            </button>
          ) : <span />}
          {next ? (
            <button onClick={() => goToChapter(next)} className="group ml-auto inline-flex items-center gap-2 text-sm text-gold hover:text-gold-luminous">
              <span className="text-right">
                <span className={`block text-[10px] uppercase tracking-[0.25em] ${themeMuted}`}>Next</span>
                <span className="font-serif">{next.name}</span>
              </span>
              <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
            </button>
          ) : <span />}
        </nav>
      </div>
    </div>
  );
}
